import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, combineLatest, map } from 'rxjs';

import { UserService, SessionStorageService, AutoReloadService, WebsocketService, NotificationService } from '@shared/services';
import { PillsPickerComponent } from '@shared/components/dynamic';
import { PillModel            } from '@shared/models';

@Injectable({
  providedIn: 'root',
})
export class PillsService {
  singleSelect: boolean;
  title:        string;

  private isInternal: boolean;
  private isCustomer: boolean;

  private reloadSubject = new BehaviorSubject<void>(null);
  reload = this.reloadSubject.asObservable();

  private pillsSubject = new BehaviorSubject<PillModel[]>([]);
  pills   = this.pillsSubject.asObservable();

  actives = this.pills.pipe(map(pills => pills
    ? pills.filter(p => p.active).map(it => this.isCustomer ? it.company_number : it.location)
    : [])
  );

  constructor(
    private userService:           UserService,
    private notificationService:   NotificationService,
    private autoReloadService:     AutoReloadService,
    private websocketService:      WebsocketService,
    private sessionStorageService: SessionStorageService,
    private zone:                  NgZone,
  ) {
    combineLatest([this.userService.currentUser, this.sessionStorageService.pills]).subscribe(([user, sourcePills]) => {
      this.title        = user.isCustomer ? 'Kundennummer' : 'Niederlassung';
      this.singleSelect = user.isCustomer;
      this.isInternal   = user.isInternal;
      this.isCustomer   = user.isCustomer;

      let filteredPills = sourcePills;
      if (user.isCustomer) {
        const resourceName = this.sessionStorageService.isTimeTracking ? 'Default' : 'CustomerInvoice';
        const companyNumbersAccessedByResource = user.customer_companies
          .filter(cc => cc.access_rights.find(a => a.resource_name === resourceName))
          .map(cc => cc.company_number);
        filteredPills = sourcePills.filter(pill => companyNumbersAccessedByResource.includes(pill.company_number));
      }

      this.pillsSubject.next(filteredPills);
    });
  }

  get pillsValue(): PillModel[] { return this.pillsSubject.value; }

  openPicker(): void {
    this.sessionStorageService.pushDynamicComponent({
      component: PillsPickerComponent.COMPONENT_NAME,
      props: {
        title:        this.title,
        singleSelect: this.singleSelect,
        popupPills:   this.pillsSubject.value.map(it=>({...it})),
        isInternal:   this.isInternal,
      }
    });
  }

  closePicker(): void {
    this.sessionStorageService.popDynamicComponent(PillsPickerComponent.COMPONENT_NAME);
  }

  checkLimits(entries: PillModel[]): boolean {
    const activeCount   = entries.filter(p =>  p.active).length;
    const unActiveCount = entries.filter(p => !p.active).length;
    if (activeCount < 30 || unActiveCount <= 1) return true;

    this.notificationService.alert('Maximal 30 Niederlassungen können Sie manuell auswählen.');
    return false;
  }

  getChangeStatus(changePills: PillModel[]): [boolean, boolean] {
    const nextPills = this.pillsSubject.value;
    let needUpdated = false;
    let needRefresh = false;
    for (const pill of nextPills) {
      const updatedPill = changePills.find(p => this.IsEqual(p, pill));
      if (updatedPill && pill.active !== updatedPill.active) {
        needUpdated = true;
        needRefresh = true;
        break;
      }
      if (updatedPill && pill.favourite !== updatedPill.favourite) {
        needUpdated = true;
      }
    }
    return [needUpdated, needRefresh];
  }

  persistPills(changePills: PillModel[], skipReload: boolean, forceUpdate: PillModel[] = null): void {
    const nextPills = forceUpdate || this.pillsSubject.value;
    for (const pill of nextPills) {
      const updatedPill = changePills.find(p => this.IsEqual(p, pill));
      if (updatedPill) {
        pill.active    = updatedPill.active;
        pill.favourite = updatedPill.favourite;
        pill.disabled  = updatedPill.disabled;
      }
    }

    this.sessionStorageService.updatePills(nextPills);
    if (!skipReload) {
      this.notificationService.wait();
      this.sessionStorageService.changePageNumber(1);
      this.zone.run(() => {
        const pilsIds = changePills.filter(e => e.active).map(e => e.id);
        this.autoReloadService.forceAutoReload();
        this.reloadSubject.next();
        this.websocketService.reloadWebSocketsChannels(pilsIds);
      });
    }
  }

  public IsEqual(item1: PillModel, item2: PillModel) {
    return (this.isCustomer && item1.company_number === item2.company_number) ||
           (this.isInternal && item1.location       === item2.location      );
  }

}
